import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useEffect, useState, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { useTenant } from '../../hooks/useTenant';
import { useAuth } from '../../hooks/useAuth';
import { useResponsive } from '../../hooks/useResponsive';
import { useRealTimeMetricsMultiple } from '../../hooks/useRealTimeMetrics';
import { useDateRange } from '../../hooks/useDateRange';

export default function OverviewView() {
  const { user } = useAuth();
  const { selectedTenantId } = useTenant();
  const responsive = useResponsive();
  const { days, label: dateLabel } = useDateRange();
  const [trendData, setTrendData] = useState<any[]>([]);
  const [deviceData, setDeviceData] = useState<any[]>([]);

  // Real-time metrics using date range from global store
  const { metrics, kpis: kpisData, trends, devices } = useRealTimeMetricsMultiple({
    metrics: { endpoint: 'overview', interval: 30000, days },
    kpis: { endpoint: 'kpi', interval: 30000, days },
    trends: { endpoint: 'trends', interval: 30000, days },
    devices: { endpoint: 'devices', interval: 60000, days },
  });

  const isLoading = metrics.isLoading || kpisData.isLoading || trends.isLoading;

  // Derive KPI cards from overview API data
  const kpis = useMemo(() => {
    const d = metrics.data;
    return [
      {
        label: 'Visitors Today',
        value: d ? String(d.visitorsToday ?? 0) : '-',
        change: d?.percentChange ?? 0,
        color: '#1e40af',
        bg: '#eff6ff',
      },
      {
        label: 'Active Now',
        value: d ? String(d.activeNow ?? 0) : '-',
        change: d?.activeNowPercentChange ?? 0,
        color: '#0369a1',
        bg: '#f0f9ff',
      },
      {
        label: 'NFC Scans',
        value: d ? String(d.totalNFCScans ?? 0) : '-',
        change: d?.totalNFCScansPercentChange ?? 0,
        color: '#7c3aed',
        bg: '#f5f3ff',
      },
      {
        label: 'QR Scans',
        value: d ? String(d.totalQRScans ?? 0) : '-',
        change: d?.totalQRScansPercentChange ?? 0,
        color: '#047857',
        bg: '#ecfdf5',
      },
    ];
  }, [metrics.data]);

  // Memoize processed trend data to avoid recalculation
  const processedTrendData = useMemo(() => {
    if (!trends.data?.trend_data) return [];

    if (days <= 1) {
      // Hourly view: keys are "YYYY-MM-DDTHH", fill all 24 hours
      const hourMap: Record<string, number> = {};
      trends.data.trend_data.forEach((m: any) => {
        const hour = parseInt(((m.date || '').split('T')[1] || '0'), 10);
        const label = `${String(hour).padStart(2, '0')}:00`;
        hourMap[label] = (hourMap[label] || 0) + (m.visits || 0);
      });
      return Array.from({ length: 24 }, (_, i) => {
        const label = `${String(i).padStart(2, '0')}:00`;
        return { day: label, visitors: hourMap[label] || 0 };
      });
    }

    return trends.data.trend_data.map((m: any) => {
      try {
        const date = parseISO(m.date);
        return {
          day: days <= 7 ? format(date, 'EEE d') : format(date, 'MMM d'),
          visitors: m.visits || 0,
        };
      } catch {
        return { day: m.date, visitors: m.visits || 0 };
      }
    });
  }, [trends.data?.trend_data, days]);

  // Memoize processed device data from backend
  const processedDeviceData = useMemo(() => {
    if (devices.data && Array.isArray(devices.data)) return devices.data;
    return [
      { device: 'Mobile', count: 0 },
      { device: 'Desktop', count: 0 },
      { device: 'Tablet', count: 0 },
    ];
  }, [devices.data]);

  useEffect(() => {
    setTrendData(processedTrendData);
  }, [processedTrendData]);

  useEffect(() => {
    setDeviceData(processedDeviceData);
  }, [processedDeviceData]);

  return (
    <div className="font-sans">
      {/* Header */}
      <div className="mb-7">
        <h1 className="text-2xl font-bold text-slate-900 m-0">Overview</h1>
        <p className="text-sm text-slate-500 mt-1">{dateLabel} · Updates every 30s</p>
      </div>

      {/* Loading or no tenant selected */}
      {user?.role === 'superadmin' && !selectedTenantId && (
        <div className="p-6 bg-red-50 rounded-xl border border-red-300 text-red-900 text-sm">
          Please select a tenant from the dropdown above to view data.
        </div>
      )}

      {isLoading && (
        <div className="p-6 text-center text-slate-500 text-sm">
          <div className="inline-block w-5 h-5 border-2 border-slate-200 border-t-blue-700 rounded-full animate-spin" />
          <p className="mt-3">Updating dashboard data...</p>
        </div>
      )}

      {!isLoading && (
        <>
          {/* KPI cards */}
          <div className={`grid gap-4 mb-7 ${
            responsive.isMedium ? 'grid-cols-2' : responsive.isSmall ? 'grid-cols-1' : 'grid-cols-4'
          }`}>
            {kpis.map(({ label, value, change, color }) => (
              <div key={label} className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
                <div className="flex justify-between items-start">
                  <p className="text-xs text-slate-500 m-0">{label}</p>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    change > 0
                      ? 'bg-green-100 text-green-600'
                      : change < 0
                        ? 'bg-red-100 text-red-600'
                        : 'bg-slate-100 text-slate-600'
                  }`}>
                    {change > 0 ? '↑' : change < 0 ? '↓' : '•'} {Math.abs(change)}%
                  </span>
                </div>
                <p className={`text-2xl font-bold m-0 mt-2`} style={{ color }}>{value}</p>
              </div>
            ))}
          </div>

          {/* Charts row */}
          <div className={`grid gap-5 ${
            responsive.isSmall ? 'grid-cols-1' : 'grid-cols-[2fr_1fr]'
          }`}>
            {/* Area chart */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
              <div className="mb-5">
                <h2 className="text-base font-semibold text-slate-900 m-0">Visitor Trend</h2>
                <p className="text-xs text-slate-400 m-0 mt-0.5">{days <= 1 ? 'Hourly unique visitors' : 'Daily unique visitors'}</p>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={trendData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                  <defs>
                    <linearGradient id="visitorsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1e40af" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#1e40af" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: '#0f172a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13 }}
                    cursor={{ stroke: '#1e40af', strokeWidth: 1, strokeDasharray: '4 4' }}
                  />
                  <Area type="monotone" dataKey="visitors" stroke="#1e40af" strokeWidth={2} fill="url(#visitorsGrad)" dot={false} activeDot={{ r: 4, fill: '#1e40af' }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Bar chart */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
              <div className="mb-5">
                <h2 className="text-base font-semibold text-slate-900 m-0">Device Split</h2>
                <p className="text-xs text-slate-400 m-0 mt-0.5">Share by device type</p>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={deviceData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }} barSize={32}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="device" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} unit="%" />
                  <Tooltip
                    contentStyle={{ background: '#0f172a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13 }}
                    cursor={{ fill: '#f1f5f9' }}
                  />
                  <Bar dataKey="count" name="Share" fill="#1e40af" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
